// @flow
import type {List} from 'immutable';
import type {SubjectV2} from 'resources/GeneratedModels/Subject/SubjectModel.v2';
import type {GuideLevelV2} from 'resources/GeneratedModels/GuideLevel/GuideLevelModel.v2';
import {mandarkEndpoint} from '@albert-io/json-api-framework/request/builder/legacy';

export type MetricsQueryParameters = {
  // Attempt number to run metrics on
  attemptNumber: number,
  // End date
  endDate: number,
  // List of string subject IDs on which to run metrics
  selectedSubject: string,
  // Student ID for whom we're running the metrics
  studentId: string,
  // Start date
  startDate: number,
  // Sort direction
  sortDirection: string
};

function _getStudentSubjectMetricsQuery({
  attemptNumber,
  endDate,
  selectedSubject,
  studentId,
  startDate
}: MetricsQueryParameters): Object {
  return mandarkEndpoint(['subjects_v2'])
    .meta({
      context: {
        start_date: startDate,
        end_date: endDate
      }
    })
    .meta({
      context: {
        student: {
          id: studentId
        }
      }
    })
    .meta(
      {
        context: {
          guess: {attempt_number: attemptNumber}
        }
      },
      attemptNumber
    )
    .meta(
      {
        context: {
          guess: {
            most_recent: true
          }
        }
      },
      !attemptNumber
    )
    .filter(
      {
        id: selectedSubject,
        included: {
          'guides_v1.guide_levels_v2': {
            'meta.count_of_questions': {
              greater_than: 0
            }
          },
          guides_v1: {
            guide_type: 'practice'
          }
        }
      },
      selectedSubject
    )
    .pageSize(400)
    .include('guides_v1.guide_levels_v2.subject_v2')
    .customQuery({
      with_meta: 'guide_v1,guide_level_v2'
    });
}

function _getStudentGuideLevelMetricsQuery({
  studentId,
  attemptNumber,
  selectedSubject,
  startDate,
  endDate,
  sortDirection = ''
}: {
  studentId: string,
  attemptNumber: number,
  selectedSubject: string,
  startDate: string,
  endDate: string,
  sortDirection: string
}): QueryBuilder {
  return mandarkEndpoint(['guide_levels_v2'])
    .include('subject_v2')
    .meta({
      context: {
        start_date: startDate,
        end_date: endDate
      }
    })
    .meta({
      context: {
        student: {
          id: studentId
        }
      }
    })
    .meta(
      {
        context: {
          guess: {attempt_number: attemptNumber}
        }
      },
      attemptNumber
    )
    .meta(
      {
        context: {
          guess: {
            most_recent: true
          }
        }
      },
      !attemptNumber
    )
    .filter(
      {
        subject_v2: selectedSubject,
        'meta.student_count_of_guesses': {
          not: 0
        }
      },
      selectedSubject
    )
    .customQuery({
      sort: sortDirection + 'meta.student_accuracy',
      with_meta: 'guide_level_v2'
    })
    .pageSize(400);
}

// *** Public functions ***
const publicInterface = {
  areStudentSubjectMetricsReady(metricsQueryParameters: MetricsQueryParameters): boolean {
    return _getStudentSubjectMetricsQuery(metricsQueryParameters).isResourcePopulated();
  },

  getStudentSubjectMetrics(metricsQueryParameters: MetricsQueryParameters): List<SubjectV2> {
    return _getStudentSubjectMetricsQuery(metricsQueryParameters).getResource();
  },

  areStudentGuideLevelMetricsReady(metricsQueryParameters: MetricsQueryParameters): boolean {
    return _getStudentGuideLevelMetricsQuery(metricsQueryParameters).isResourcePopulated();
  },

  getStudentGuideLevelMetrics(metricsQueryParameters: MetricsQueryParameters): List<GuideLevelV2> {
    return _getStudentGuideLevelMetricsQuery(metricsQueryParameters).getResource();
  }
};

export default publicInterface;
